import bindAll from 'lodash.bindall';
import debounce from 'lodash.debounce';
import defaultsDeep from 'lodash.defaultsdeep';
import makeToolboxXML from '../lib/make-toolbox-xml';
import PropTypes from 'prop-types';
import React from 'react';
import VMScratchBlocks from '../lib/blocks';
import VM from '@scratch/scratch-vm';

import analytics from '../lib/analytics';
import log from '../lib/log.js';
import Prompt from './prompt.jsx';
import BlocksComponent from '../components/blocks/blocks.jsx';
import ExtensionLibrary from './extension-library.jsx';
import extensionData from '../lib/libraries/extensions/index.jsx';
import CustomProcedures from './custom-procedures.jsx';
import errorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import {BLOCKS_DEFAULT_SCALE, STAGE_DISPLAY_SIZES} from '../lib/layout-constants';
import DropAreaHOC from '../lib/drop-area-hoc.jsx';
import DragConstants from '../lib/drag-constants';
import {ensureReadOnlyStyle, READ_ONLY_BODY_CLASS} from '../playground/jm-read-only.js';
import defineDynamicBlock from '../lib/define-dynamic-block';
import {DEFAULT_MODE, getColorsForMode, colorModeMap} from '../lib/settings/color-mode';
import {CAT_BLOCKS_THEME} from '../lib/settings/theme';
import {
    injectExtensionBlockIcons,
    injectExtensionCategoryMode,
    getExtensionColors
} from '../lib/settings/color-mode/blockHelpers';

import {connect} from 'react-redux';
import {updateToolbox} from '../reducers/toolbox';
import {activateColorPicker} from '../reducers/color-picker';
import {closeExtensionLibrary, openSoundRecorder, openConnectionModal} from '../reducers/modals';
import {activateCustomProcedures, deactivateCustomProcedures} from '../reducers/custom-procedures';
import {setConnectionModalExtensionId} from '../reducers/connection-modal';
import {updateMetrics} from '../reducers/workspace-metrics';
import {isTimeTravel2020} from '../reducers/time-travel';
import {
    activateTab,
    SOUNDS_TAB_INDEX
} from '../reducers/editor-tab';

const vmScriptRootId = (vmBlocks, blockId) => {
    let block = vmBlocks[blockId];
    const seen = new Set();
    while (block && block.parent && !seen.has(block.id)) {
        seen.add(block.id);
        block = vmBlocks[block.parent];
    }
    return block && block.topLevel && !block.shadow ? block.id : null;
};

// scratch-blocks gives each block comment bubble the DOM id `${blockId}_comment`.
const BLOCK_COMMENT_SUFFIX = '_comment';

const addFunctionListener = (object, property, callback) => {
    const oldFn = object[property];
    object[property] = function (...args) {
        const result = oldFn.apply(this, args);
        callback.apply(this, result);
        return result;
    };
};

const DroppableBlocks = DropAreaHOC([
    DragConstants.BACKPACK_CODE
])(BlocksComponent);

class Blocks extends React.Component {
    constructor (props) {
        super(props);
        this.ScratchBlocks = VMScratchBlocks(props.vm);
        bindAll(this, [
            'attachVM',
            'detachVM',
            'getToolboxXML',
            'handleCategorySelected',
            'handleConnectionModalStart',
            'handleDrop',
            'handleStatusButtonUpdate',
            'handleOpenSoundRecorder',
            'handlePromptStart',
            'handlePromptCallback',
            'handlePromptClose',
            'handleCustomProceduresClose',
            'onScriptGlowOn',
            'onScriptGlowOff',
            'onBlockGlowOn',
            'onBlockGlowOff',
            'handleMonitorsUpdate',
            'handleExtensionAdded',
            'handleBlocksInfoUpdate',
            'onTargetsUpdate',
            'onVisualReport',
            'onWorkspaceUpdate',
            'onWorkspaceMetricsChange',
            'getBusyRootId',
            'getAffectedRootIds',
            'applyScripts',
            'syncBlockCommentsToVm',
            'applyVariables',
            'getBusyCommentId',
            'applyComments',
            'applyWorkspaceComment',
            'applyBlockComment',
            'disposeComment',
            'applyBlockCommentLocations',
            'setLockedBlocks',
            'renderSilently',
            'setBlocks',
            'setLocale'
        ]);
        this.ScratchBlocks.dialog.setPrompt(this.handlePromptStart);
        this.ScratchBlocks.ScratchVariables.setPromptHandler(
            this.handlePromptStart
        );
        this.ScratchBlocks.StatusIndicatorLabel.statusButtonCallback = this.handleConnectionModalStart;
        this.ScratchBlocks.recordSoundCallback = this.handleOpenSoundRecorder;

        this.state = {
            prompt: null
        };
        this.onTargetsUpdate = debounce(this.onTargetsUpdate, 100);
        this.toolboxUpdateQueue = [];
    }
    componentDidMount () {
        this.ScratchBlocks = VMScratchBlocks(this.props.vm, this.props.useCatBlocks);
        this.ScratchBlocks.dialog.setPrompt(this.handlePromptStart);
        this.ScratchBlocks.StatusIndicatorLabel.statusButtonCallback = this.handleConnectionModalStart;
        this.ScratchBlocks.recordSoundCallback = this.handleOpenSoundRecorder;

        this.ScratchBlocks.FieldColourSlider.activateEyedropper_ = this.props.onActivateColorPicker;
        this.ScratchBlocks.ScratchProcedures.externalProcedureDefCallback = this.props.onActivateCustomProcedures;
        this.ScratchBlocks.ScratchMsgs.setLocale(this.props.locale);

        const workspaceConfig = defaultsDeep({},
            Blocks.defaultOptions,
            this.props.options,
            {
                rtl: this.props.isRtl,
                toolbox: this.props.toolboxXML,
                theme: new this.ScratchBlocks.Theme(
                    this.props.colorMode,
                    getColorsForMode(this.props.colorMode)
                ),
                // TODO: use scratch-blocks constants instead of bare strings
                scratchTheme: this.props.useCatBlocks ? 'catblocks' : 'classic'
            }
        );
        this.workspace = this.ScratchBlocks.inject(this.blocks, workspaceConfig);
        if (this.props.readOnly && this.workspace) {
            if (this.workspace.options) this.workspace.options.readOnly = true;
            ensureReadOnlyStyle();
            if (typeof document !== 'undefined' && document.body) {
                document.body.classList.add(READ_ONLY_BODY_CLASS);
            }
        }
        this.lockedBlockIds = new Set();
        this.lockedRootIds = new Set();
        this.installRemoteLockGuards();
        window.__JM_BLOCKLY__ = {
            isDragging: () => !!(this.workspace && this.workspace.isDragging && this.workspace.isDragging()),
            getWorkspace: () => this.workspace,
            getBusyRootId: this.getBusyRootId,
            getAffectedRootIds: this.getAffectedRootIds,
            applyScripts: this.applyScripts,
            getBusyCommentId: this.getBusyCommentId,
            applyComments: this.applyComments,
            setLockedBlocks: this.setLockedBlocks
        };
        window.dispatchEvent(new CustomEvent('jm:blockly-ready'));
        this.workspace.registerToolboxCategoryCallback(
            'VARIABLE',
            this.ScratchBlocks.ScratchVariables.getVariablesCategory
        );
        this.workspace.registerToolboxCategoryCallback(
            'PROCEDURE',
            this.ScratchBlocks.ScratchProcedures.getProceduresCategory
        );

        this.toolboxUpdateChangeListener = event => {
            if (
                event.type === this.ScratchBlocks.Events.VAR_CREATE ||
                event.type === this.ScratchBlocks.Events.VAR_RENAME ||
                event.type === this.ScratchBlocks.Events.VAR_DELETE ||
                (event.type === this.ScratchBlocks.Events.BLOCK_DELETE &&
                    event.oldJson.type === 'procedures_definition') ||
                // Only refresh the toolbox when procedure block creations are
                // triggered by undoing a deletion (implied by recordUndo being
                // false on the event).
                (event.type === this.ScratchBlocks.Events.BLOCK_CREATE &&
                    event.json.type === 'procedures_definition' &&
                    !event.recordUndo)
            ) {
                this.requestToolboxUpdate();
            }
        };
        this.workspace.addChangeListener(this.toolboxUpdateChangeListener);

        // Register buttons under new callback keys for creating variables,
        // lists, and procedures from extensions.

        const toolboxWorkspace = this.workspace.getFlyout().getWorkspace();

        const varListButtonCallback = type =>
            (() => this.ScratchBlocks.ScratchVariables.createVariable(this.workspace, null, type));
        const procButtonCallback = () => {
            this.ScratchBlocks.ScratchProcedures.createProcedureDefCallback(this.workspace);
        };

        toolboxWorkspace.registerButtonCallback('MAKE_A_VARIABLE', varListButtonCallback(''));
        toolboxWorkspace.registerButtonCallback('MAKE_A_LIST', varListButtonCallback('list'));
        toolboxWorkspace.registerButtonCallback('MAKE_A_PROCEDURE', procButtonCallback);

        // Store the xml of the toolbox that is actually rendered.
        // This is used in componentDidUpdate instead of prevProps, because
        // the xml can change while e.g. on the costumes tab.
        this._renderedToolboxXML = this.props.toolboxXML;

        // @todo change this when blockly supports UI events
        addFunctionListener(this.workspace, 'translate', this.onWorkspaceMetricsChange);
        addFunctionListener(this.workspace, 'zoom', this.onWorkspaceMetricsChange);
        this.workspace.getToolbox().selectItemByPosition(0);

        this.attachVM();
        // Only update blocks/vm locale when visible to avoid sizing issues
        // If locale changes while not visible it will get handled in didUpdate
        if (this.props.isVisible) {
            this.setLocale();
        }

        window.addEventListener('load-extension', () => {
            this.props.vm.extensionManager.loadExtensionURL('faceSensing').then(() => {
                this.handleCategorySelected('faceSensing');
            });
        });
    }
    shouldComponentUpdate (nextProps, nextState) {
        return (
            this.state.prompt !== nextState.prompt ||
            this.props.isVisible !== nextProps.isVisible ||
            this._renderedToolboxXML !== nextProps.toolboxXML ||
            this.props.extensionLibraryVisible !== nextProps.extensionLibraryVisible ||
            this.props.customProceduresVisible !== nextProps.customProceduresVisible ||
            this.props.locale !== nextProps.locale ||
            this.props.anyModalVisible !== nextProps.anyModalVisible ||
            this.props.stageSize !== nextProps.stageSize
        );
    }
    componentDidUpdate (prevProps) {
        // If any modals are open, call hideChaff to close z-indexed field editors
        if (this.props.anyModalVisible && !prevProps.anyModalVisible) {
            this.ScratchBlocks.hideChaff();
        }

        // Only rerender the toolbox when the blocks are visible and the xml is
        // different from the previously rendered toolbox xml.
        // Do not check against prevProps.toolboxXML because that may not have been rendered.
        if (this.props.isVisible && this.props.toolboxXML !== this._renderedToolboxXML) {
            this.requestToolboxUpdate();
        }

        if (this.props.isVisible === prevProps.isVisible) {
            if (this.props.stageSize !== prevProps.stageSize) {
                // force workspace to redraw for the new stage size
                window.dispatchEvent(new Event('resize'));
            }
            return;
        }
        // @todo hack to resize blockly manually in case resize happened while hidden
        // @todo hack to reload the workspace due to gui bug #413
        if (this.props.isVisible) { // Scripts tab
            this.workspace.setVisible(true);
            if (prevProps.locale !== this.props.locale || this.props.locale !== this.props.vm.getLocale()) {
                // call setLocale if the locale has changed, or changed while the blocks were hidden.
                // vm.getLocale() will be out of sync if locale was changed while not visible
                this.setLocale();
            } else {
                this.props.vm.refreshWorkspace();
            }

            window.dispatchEvent(new Event('resize'));
        } else {
            this.workspace.setVisible(false);
        }
    }
    componentWillUnmount () {
        this.detachVM();
        // Hide any open field editor and move Blockly focus to the workspace
        // root before disposing. Without this, BlockSvg.dispose() detects the
        // focused element is inside a block and schedules a stale
        // setTimeout(() => focusTree(workspace)), which fires after the
        // workspace is unregistered and throws
        // "Attempted to focus unregistered tree" (scratch-blocks#3460).
        //
        // focusNode(workspace) — not focusTree(workspace) — is used here
        // because focusTree would restore focus to whatever was previously
        // focused in this workspace (likely the same block about to be
        // disposed). focusNode pins focus to the workspace root directly,
        // ensuring no block is focused when dispose() runs.
        this.ScratchBlocks.WidgetDiv.hide();
        this.ScratchBlocks.getFocusManager().focusNode(this.workspace);
        this.workspace.dispose();
        window.__JM_BLOCKLY__ = null;
        clearTimeout(this.toolboxUpdateTimeout);

        // Clear the flyout blocks so that they can be recreated on mount.
        this.props.vm.clearFlyoutBlocks();
    }
    requestToolboxUpdate () {
        clearTimeout(this.toolboxUpdateTimeout);
        this.toolboxUpdateTimeout = setTimeout(() => {
            this.updateToolbox();
        }, 0);
    }
    setLocale () {
        this.ScratchBlocks.ScratchMsgs.setLocale(this.props.locale);
        this.props.vm.setLocale(this.props.locale, this.props.messages)
            .then(() => {
                this.workspace.getFlyout().setRecyclingEnabled(false);
                this.props.vm.refreshWorkspace();
                this.requestToolboxUpdate();
                this.withToolboxUpdates(() => {
                    this.workspace.getFlyout().setRecyclingEnabled(true);
                });
            });
    }

    updateToolbox () {
        this.toolboxUpdateTimeout = false;

        // Recycled flyout blocks keep their old ids, but sprite-specific ones (e.g. x position)
        // embed the sprite's id from the toolbox XML, and their monitors are keyed by it.
        if (this.props.toolboxXML !== this._renderedToolboxXML) {
            this.workspace.getFlyout().setRecyclingEnabled(false);
            this.toolboxUpdateQueue.push(() => this.workspace.getFlyout().setRecyclingEnabled(true));
        }

        const scale = this.workspace.getFlyout().getWorkspace().scale;
        const selectedCategoryName = this.workspace
            .getToolbox()
            .getSelectedItem()
            .getName();
        const selectedCategoryScrollPosition =
            this.workspace
                .getFlyout()
                .getCategoryScrollPosition(selectedCategoryName) * scale;
        const offsetWithinCategory =
            this.workspace.getFlyout().getWorkspace()
                .getMetrics().viewTop -
            selectedCategoryScrollPosition;

        this.workspace.updateToolbox(this.props.toolboxXML);
        this.workspace.getToolbox().runAfterRerender(() => {
            const newCategoryScrollPosition = this.workspace
                .getFlyout()
                .getCategoryScrollPosition(selectedCategoryName);
            if (newCategoryScrollPosition) {
                this.workspace
                    .getFlyout()
                    .getWorkspace()
                    .scrollbar.setY(
                        (newCategoryScrollPosition * scale) + offsetWithinCategory
                    );
            }
        });
        this.workspace.getToolbox().forceRerender();
        this._renderedToolboxXML = this.props.toolboxXML;

        const queue = this.toolboxUpdateQueue;
        this.toolboxUpdateQueue = [];
        queue.forEach(fn => fn());
    }

    withToolboxUpdates (fn) {
        // if there is a queued toolbox update, we need to wait
        if (this.toolboxUpdateTimeout) {
            this.toolboxUpdateQueue.push(fn);
        } else {
            fn();
        }
    }

    attachVM () {
        this.workspace.addChangeListener(this.props.vm.blockListener);
        this.flyoutWorkspace = this.workspace
            .getFlyout()
            .getWorkspace();
        this.flyoutWorkspace.addChangeListener(this.props.vm.flyoutBlockListener);
        this.flyoutWorkspace.addChangeListener(this.props.vm.monitorBlockListener);
        this.props.vm.addListener('SCRIPT_GLOW_ON', this.onScriptGlowOn);
        this.props.vm.addListener('SCRIPT_GLOW_OFF', this.onScriptGlowOff);
        this.props.vm.addListener('BLOCK_GLOW_ON', this.onBlockGlowOn);
        this.props.vm.addListener('BLOCK_GLOW_OFF', this.onBlockGlowOff);
        this.props.vm.addListener('VISUAL_REPORT', this.onVisualReport);
        this.props.vm.addListener('workspaceUpdate', this.onWorkspaceUpdate);
        this.props.vm.addListener('targetsUpdate', this.onTargetsUpdate);
        this.props.vm.addListener('MONITORS_UPDATE', this.handleMonitorsUpdate);
        this.props.vm.addListener('EXTENSION_ADDED', this.handleExtensionAdded);
        this.props.vm.addListener('BLOCKSINFO_UPDATE', this.handleBlocksInfoUpdate);
        this.props.vm.addListener('PERIPHERAL_CONNECTED', this.handleStatusButtonUpdate);
        this.props.vm.addListener('PERIPHERAL_DISCONNECTED', this.handleStatusButtonUpdate);
    }
    detachVM () {
        this.props.vm.removeListener('SCRIPT_GLOW_ON', this.onScriptGlowOn);
        this.props.vm.removeListener('SCRIPT_GLOW_OFF', this.onScriptGlowOff);
        this.props.vm.removeListener('BLOCK_GLOW_ON', this.onBlockGlowOn);
        this.props.vm.removeListener('BLOCK_GLOW_OFF', this.onBlockGlowOff);
        this.props.vm.removeListener('VISUAL_REPORT', this.onVisualReport);
        this.props.vm.removeListener('workspaceUpdate', this.onWorkspaceUpdate);
        this.props.vm.removeListener('targetsUpdate', this.onTargetsUpdate);
        this.props.vm.removeListener('MONITORS_UPDATE', this.handleMonitorsUpdate);
        this.props.vm.removeListener('EXTENSION_ADDED', this.handleExtensionAdded);
        this.props.vm.removeListener('BLOCKSINFO_UPDATE', this.handleBlocksInfoUpdate);
        this.props.vm.removeListener('PERIPHERAL_CONNECTED', this.handleStatusButtonUpdate);
        this.props.vm.removeListener('PERIPHERAL_DISCONNECTED', this.handleStatusButtonUpdate);
    }

    updateToolboxBlockValue (id, value) {
        this.withToolboxUpdates(() => {
            const block = this.workspace
                .getFlyout()
                .getWorkspace()
                .getBlockById(id);
            if (block) {
                block.inputList[0].fieldRow[0].setValue(value);
            }
        });
    }

    onTargetsUpdate () {
        if (this.props.vm.editingTarget && this.workspace.getFlyout()) {
            ['glide', 'move', 'set'].forEach(prefix => {
                this.updateToolboxBlockValue(`${prefix}x`, Math.round(this.props.vm.editingTarget.x).toString());
                this.updateToolboxBlockValue(`${prefix}y`, Math.round(this.props.vm.editingTarget.y).toString());
            });
        }
    }
    onWorkspaceMetricsChange () {
        const target = this.props.vm.editingTarget;
        if (target && target.id) {
            // Dispatch updateMetrics later, since onWorkspaceMetricsChange may be (very indirectly)
            // called from a reducer, i.e. when you create a custom procedure.
            // TODO: Is this a vehement hack?
            setTimeout(() => {
                this.props.updateMetrics({
                    targetID: target.id,
                    scrollX: this.workspace.scrollX,
                    scrollY: this.workspace.scrollY,
                    scale: this.workspace.scale
                });
            }, 0);
        }
    }
    onScriptGlowOn (data) {
        this.ScratchBlocks.glowStack(data.id, true);
    }
    onScriptGlowOff (data) {
        this.ScratchBlocks.glowStack(data.id, false);
    }
    onBlockGlowOn (/* data */) {
        // No-op, support may be added in the future
    }
    onBlockGlowOff (/* data */) {
        // No-op, support may be added in the future
    }
    onVisualReport (data) {
        this.ScratchBlocks.reportValue(data.id, data.value);
    }
    getToolboxXML () {
        // Use try/catch because this requires digging pretty deep into the VM
        // Code inside intentionally ignores several error situations (no stage, etc.)
        // Because they would get caught by this try/catch
        try {
            let {editingTarget: target, runtime} = this.props.vm;
            const stage = runtime.getTargetForStage();
            if (!target) target = stage; // If no editingTarget, use the stage

            const stageCostumes = stage.getCostumes();
            const targetCostumes = target.getCostumes();
            const targetSounds = target.getSounds();
            const dynamicBlocksXML = injectExtensionCategoryMode(
                this.props.vm.runtime.getBlocksXML(target),
                this.props.colorMode
            );
            return makeToolboxXML(false, target.isStage, target.id, dynamicBlocksXML,
                targetCostumes[targetCostumes.length - 1].name,
                stageCostumes[stageCostumes.length - 1].name,
                targetSounds.length > 0 ? targetSounds[targetSounds.length - 1].name : '',
                getColorsForMode(this.props.colorMode)
            );
        } catch {
            return null;
        }
    }
    getBusyRootId () {
        const ws = this.workspace;
        if (!ws) return null;
        if (ws.isDragging()) return '*';
        const active = document.activeElement;
        const commentBubble = active && active.closest ? active.closest(`[id$="${BLOCK_COMMENT_SUFFIX}"]`) : null;
        const commentedBlock = commentBubble &&
            ws.getBlockById(commentBubble.id.slice(0, -BLOCK_COMMENT_SUFFIX.length));
        if (commentedBlock) return commentedBlock.getRootBlock().id;
        const SB = this.ScratchBlocks;
        if (!SB.WidgetDiv.isVisible() && !SB.DropDownDiv.isVisible()) return null;
        const selected = SB.getSelected();
        return selected && typeof selected.getRootBlock === 'function' && selected.workspace === ws ?
            selected.getRootBlock().id :
            null;
    }
    getAffectedRootIds (blockIds) {
        const ws = this.workspace;
        const target = this.props.vm.editingTarget;
        if (!ws || !target) return [];
        const vmBlocks = target.blocks._blocks;
        const roots = new Set();
        for (const id of blockIds) {
            const block = ws.getBlockById(id);
            if (block) roots.add(block.getRootBlock().id);
            const vmRoot = vmScriptRootId(vmBlocks, id);
            if (vmRoot) roots.add(vmRoot);
        }
        return [...roots];
    }
    scriptMatchesVm (rootId, vmBlocks) {
        const ws = this.workspace;
        const root = ws.getBlockById(rootId);
        if (!root) return false;
        for (const block of root.getDescendants(false)) {
            const vmBlock = vmBlocks[block.id];
            const parent = block.getParent();
            if (!vmBlock || (vmBlock.parent || null) !== (parent ? parent.id : null)) return false;
        }
        const pending = [rootId];
        const seen = new Set();
        while (pending.length) {
            const id = pending.pop();
            if (!id || seen.has(id)) continue;
            seen.add(id);
            const vmBlock = vmBlocks[id];
            if (!vmBlock) continue;
            const block = ws.getBlockById(id);
            if (!block) return false;
            if (!vmBlock.shadow) {
                const parent = block.getParent();
                if ((vmBlock.parent || null) !== (parent ? parent.id : null)) return false;
            }
            for (const name in vmBlock.fields) {
                const field = block.getField(name);
                const vmField = vmBlock.fields[name];
                const expected = vmField.id ?? vmField.value;
                if (!field || String(field.getValue()) !== String(expected)) return false;
            }
            pending.push(vmBlock.next);
            for (const name in vmBlock.inputs) pending.push(vmBlock.inputs[name].block);
        }
        return true;
    }
    syncBlockCommentsToVm (rootBlock, target) {
        const commentType = this.ScratchBlocks.icons.IconType.COMMENT;
        for (const block of rootBlock.getDescendants(false)) {
            const vmBlock = target.blocks._blocks[block.id];
            const comment = vmBlock && vmBlock.comment && target.comments[vmBlock.comment];
            const icon = comment && block.getIcon(commentType);
            const location = icon && icon.getBubbleLocation();
            if (location) {
                comment.x = location.x;
                comment.y = location.y;
            }
        }
    }
    collectVmScriptBlocks (vmRoots, vmBlocks) {
        const result = new Set();
        const stack = [...vmRoots];
        while (stack.length) {
            const id = stack.pop();
            const vmBlock = id && vmBlocks[id];
            if (!vmBlock || vmBlock.shadow || result.has(id)) continue;
            result.add(id);
            if (vmBlock.next) stack.push(vmBlock.next);
            for (const name in vmBlock.inputs) {
                const input = vmBlock.inputs[name];
                if (input.block && input.block !== input.shadow) stack.push(input.block);
            }
        }
        return result;
    }
    reconcileNative (vmRoots, desired, vmBlocks) {
        const ws = this.workspace;
        const SB = this.ScratchBlocks;
        const plan = [];
        for (const id of desired) {
            const block = ws.getBlockById(id);
            if (!block) return false;
            const vmBlock = vmBlocks[id];
            const myConn = block.previousConnection || block.outputConnection;
            if (!vmBlock.parent) {
                plan.push({myConn, targetConn: null});
                continue;
            }
            const parentBlock = ws.getBlockById(vmBlock.parent);
            const parentVm = vmBlocks[vmBlock.parent];
            if (!parentBlock || !parentVm || !myConn) return false;
            let targetConn = null;
            if (parentVm.next === id) {
                targetConn = parentBlock.nextConnection;
            } else {
                for (const name in parentVm.inputs) {
                    if (parentVm.inputs[name].block === id) {
                        const input = parentBlock.getInput(name);
                        targetConn = input && input.connection;
                        break;
                    }
                }
            }
            if (!targetConn) return false;
            plan.push({myConn, targetConn});
        }
        for (const {myConn, targetConn} of plan) {
            if (!myConn) continue;
            if (targetConn) {
                if (myConn.targetConnection === targetConn) continue;
                if (myConn.isConnected()) myConn.disconnect();
                targetConn.connect(myConn);
            } else if (myConn.isConnected()) {
                myConn.disconnect();
            }
        }
        for (const id of vmRoots) {
            const vmBlock = vmBlocks[id];
            const block = vmBlock && !vmBlock.parent && ws.getBlockById(id);
            if (block) block.moveTo(new SB.utils.Coordinate(vmBlock.x || 0, vmBlock.y || 0));
        }
        return true;
    }
    syncFieldsToVm (vmRoots, vmBlocks) {
        const ws = this.workspace;
        const stack = [...vmRoots];
        const seen = new Set();
        while (stack.length) {
            const id = stack.pop();
            const vmBlock = id && vmBlocks[id];
            if (!vmBlock || seen.has(id)) continue;
            seen.add(id);
            const block = ws.getBlockById(id);
            if (block) {
                for (const name in vmBlock.fields) {
                    const field = block.getField(name);
                    if (!field) continue;
                    const vmField = vmBlock.fields[name];
                    const expected = vmField.id ?? vmField.value;
                    if (String(field.getValue()) !== String(expected)) field.setValue(expected);
                }
            }
            if (vmBlock.next) stack.push(vmBlock.next);
            for (const name in vmBlock.inputs) {
                const input = vmBlock.inputs[name];
                if (input.block) stack.push(input.block);
                if (input.shadow && input.shadow !== input.block) stack.push(input.shadow);
            }
        }
    }
    recreateScripts (rootIds, vmRoots, vmBlocks, target, SB, ws) {
        const toDispose = new Set();
        for (const id of rootIds) {
            const root = ws.getBlockById(id)?.getRootBlock();
            if (root) toDispose.add(root);
        }
        if (toDispose.size === 0 && vmRoots.length === 0) return true;
        const dom = SB.utils.xml.textToDom(
            `<xml xmlns="http://www.w3.org/1999/xhtml">${
                vmRoots.map(id => target.blocks.blockToXML(id, target.comments)).join('')
            }</xml>`
        );
        const missingVariableIds = [...dom.querySelectorAll('field[variabletype]')]
            .map(field => field.getAttribute('id'))
            .filter(id => id && !ws.getVariableById(id));
        if (missingVariableIds.length > 0 &&
            (!this.applyVariables(missingVariableIds) ||
                missingVariableIds.some(id => !ws.getVariableById(id)))) {
            return false;
        }
        const focused = SB.getFocusManager().getFocusedNode();
        if (focused && typeof focused.getRootBlock === 'function' &&
            toDispose.has(focused.getRootBlock()) &&
            ws.getInjectionDiv().contains(document.activeElement)) {
            SB.getFocusManager().focusNode(ws);
        }
        const touchesProcedures = [...toDispose].some(b => b.type === 'procedures_definition') ||
            vmRoots.some(id => vmBlocks[id] && vmBlocks[id].opcode === 'procedures_definition');
        for (const block of toDispose) block.dispose(false);
        SB.Xml.domToWorkspace(dom, ws);
        this.renderSilently();
        if (!vmRoots.every(id => this.scriptMatchesVm(id, vmBlocks))) return false;
        if (touchesProcedures) this.requestToolboxUpdate();
        return true;
    }
    applyScripts (blockIds) {
        const ws = this.workspace;
        const target = this.props.vm.editingTarget;
        if (!ws || !target || ws.isDragging()) return false;
        const SB = this.ScratchBlocks;
        const vmBlocks = target.blocks._blocks;
        const rootIds = this.getAffectedRootIds(blockIds);
        if (rootIds.length === 0) return true;
        const vmRoots = rootIds.filter(id => vmScriptRootId(vmBlocks, id) === id);

        const desired = this.collectVmScriptBlocks(vmRoots, vmBlocks);
        const current = new Set();
        for (const id of rootIds) {
            const root = ws.getBlockById(id);
            if (!root) continue;
            for (const block of root.getRootBlock().getDescendants(false)) {
                if (!block.isShadow()) current.add(block.id);
            }
        }
        const sameBlocks = desired.size === current.size &&
            [...desired].every(id => current.has(id));

        let ok = true;
        ws.setResizesEnabled(false);
        SB.Events.disable();
        try {
            if (sameBlocks && this.reconcileNative(vmRoots, desired, vmBlocks)) {
                this.syncFieldsToVm(vmRoots, vmBlocks);
                this.renderSilently();
                for (const id of vmRoots) {
                    const block = ws.getBlockById(id);
                    if (block) this.syncBlockCommentsToVm(block, target);
                }
                ok = vmRoots.every(id => this.scriptMatchesVm(id, vmBlocks));
            } else {
                ok = false;
            }
            if (!ok) ok = this.recreateScripts(rootIds, vmRoots, vmBlocks, target, SB, ws);
        } catch (error) {
            log.error(error);
            ok = false;
        } finally {
            SB.Events.enable();
            ws.setResizesEnabled(true);
        }
        if (!ok) return false;
        ws.clearUndo();
        this.lockAllBlocksIfReadOnly();
        return true;
    }
    // Render with events disabled: triggerQueuedRenders fires comment-move/bump events that would otherwise echo as local edits.
    renderSilently () {
        this.ScratchBlocks.renderManagement.triggerQueuedRenders(this.workspace);
    }
    lockAllBlocksIfReadOnly () {
        if (!this.props.readOnly || !this.workspace || !this.workspace.getAllBlocks) return;
        this.workspace.getAllBlocks(false).forEach(block => {
            if (block.setMovable) block.setMovable(false);
            if (block.setEditable) block.setEditable(false);
            if (block.setDeletable) block.setDeletable(false);
        });
    }
    findVmVariable (id) {
        const vm = this.props.vm;
        const target = vm.editingTarget;
        const local = target && !target.isStage ? target.variables[id] : null;
        if (local) return {variable: local, isLocal: true};
        const stage = vm.runtime.getTargetForStage();
        const global = stage ? stage.variables[id] : null;
        return global ? {variable: global, isLocal: false} : null;
    }
    applyVariables (variableIds) {
        const ws = this.workspace;
        if (!ws) return false;
        const SB = this.ScratchBlocks;
        const map = ws.getVariableMap();
        const changes = [];
        for (const id of variableIds) {
            const found = this.findVmVariable(id);
            const shown = map.getVariableById(id);
            if (!found) {
                if (!shown) continue;
                if (SB.Variables.getVariableUsesById(ws, id).length > 0) return false;
                changes.push(() => map.deleteVariable(shown));
                continue;
            }
            const {variable, isLocal} = found;
            const type = variable.type || '';
            const sameName = map.getVariable(variable.name, type);
            if (sameName && sameName.getId() !== id) return false;
            if (shown) {
                if (shown.getName() !== variable.name) changes.push(() => map.renameVariable(shown, variable.name));
                continue;
            }
            const VariableModel = SB.registry.getClass(SB.registry.Type.VARIABLE_MODEL, SB.registry.DEFAULT, true);
            changes.push(() => map.addVariable(
                new VariableModel(ws, variable.name, type, id, isLocal, !!variable.isCloud)
            ));
        }
        if (changes.length === 0) return true;
        SB.Events.disable();
        try {
            for (const change of changes) change();
        } catch (error) {
            log.error(error);
            return false;
        } finally {
            SB.Events.enable();
        }
        this.requestToolboxUpdate();
        return true;
    }
    getBusyCommentId () {
        const ws = this.workspace;
        const active = document.activeElement;
        if (!ws || !active || !active.closest) return null;
        const wsComment = ws.getTopComments(false).find(c => c.getSvgRoot().contains(active));
        if (wsComment) return wsComment.id;
        const bubble = active.closest(`[id$="${BLOCK_COMMENT_SUFFIX}"]`);
        if (!bubble) return null;
        const blockId = bubble.id.slice(0, -BLOCK_COMMENT_SUFFIX.length);
        const target = this.props.vm.editingTarget;
        const owner = target && Object.values(target.comments).find(c => c.blockId === blockId);
        return owner ? owner.id : null;
    }
    applyComments (commentIds) {
        const ws = this.workspace;
        const target = this.props.vm.editingTarget;
        if (!ws || !target || ws.isDragging()) return false;
        const SB = this.ScratchBlocks;
        const {Coordinate, Size} = SB.utils;
        SB.Events.disable();
        try {
            for (const id of commentIds) {
                const data = target.comments[id];
                if (!data) {
                    this.disposeComment(id);
                    continue;
                }
                if (data.blockId) this.applyBlockComment(data, Coordinate, Size);
                else this.applyWorkspaceComment(id, data, Coordinate, Size);
            }
            this.renderSilently();
        } catch (error) {
            log.error(error);
            return false;
        } finally {
            SB.Events.enable();
        }
        return true;
    }
    applyWorkspaceComment (id, data, Coordinate, Size) {
        const ws = this.workspace;
        const existing = ws.getCommentById(id);
        if (!existing) {
            const SB = this.ScratchBlocks;
            const dom = SB.utils.xml.textToDom(
                `<xml xmlns="http://www.w3.org/1999/xhtml">${data.toXML()}</xml>`
            );
            SB.Xml.domToWorkspace(dom, ws);
            return;
        }
        if (existing.getText() !== data.text) existing.setText(data.text);
        existing.setSize(new Size(data.width, data.height));
        existing.moveTo(new Coordinate(data.x, data.y));
        existing.setCollapsed(!!data.minimized);
    }
    applyBlockComment (data, Coordinate, Size) {
        const block = this.workspace.getBlockById(data.blockId);
        if (!block) return;
        let icon = block.getIcon(this.ScratchBlocks.icons.IconType.COMMENT);
        if (!icon) {
            block.setCommentText(data.text);
            icon = block.getIcon(this.ScratchBlocks.icons.IconType.COMMENT);
            if (!icon) return;
        } else if (icon.getText() !== data.text) {
            icon.setText(data.text);
        }
        icon.setBubbleSize(new Size(data.width, data.height));
        icon.setBubbleLocation(new Coordinate(data.x, data.y));
        icon.setBubbleVisible(!data.minimized);
    }
    disposeComment (id) {
        const wsComment = this.workspace.getCommentById(id);
        if (wsComment) {
            wsComment.dispose();
            return;
        }
        const target = this.props.vm.editingTarget;
        const owner = target && Object.values(target.comments).find(c => c.id === id);
        const block = owner && owner.blockId && this.workspace.getBlockById(owner.blockId);
        if (block) block.setCommentText(null);
    }
    applyBlockCommentLocations () {
        const target = this.props.vm.editingTarget;
        if (!target || !this.workspace) return;
        const {Coordinate, Size} = this.ScratchBlocks.utils;
        for (const data of Object.values(target.comments || {})) {
            if (data.blockId) this.applyBlockComment(data, Coordinate, Size);
        }
    }
    installRemoteLockGuards () {
        const checker = this.workspace && this.workspace.connectionChecker;
        if (checker && typeof checker.doDragChecks === 'function' && !checker.jmLockWrapped) {
            const original = checker.doDragChecks.bind(checker);
            checker.doDragChecks = (a, b, distance) =>
                !this.isConnectionLocked(a) && !this.isConnectionLocked(b) && original(a, b, distance);
            checker.jmLockWrapped = true;
        }
        if (typeof document !== 'undefined' && !document.getElementById('jm-remote-lock-style')) {
            const style = document.createElement('style');
            style.id = 'jm-remote-lock-style';
            style.textContent = '.blocklyRemoteLocked{opacity:0.55;}';
            document.head.appendChild(style);
        }
    }
    setLockedBlocks (draggedBlockIds) {
        const ws = this.workspace;
        if (!ws || !this.lockedBlockIds || (ws.options && ws.options.readOnly)) return;
        const nextBlocks = new Set();
        const nextRoots = new Set();
        for (const id of draggedBlockIds || []) {
            const block = ws.getBlockById(id);
            if (!block) continue;
            const root = block.getRootBlock();
            nextRoots.add(root.id);
            for (const descendant of root.getDescendants(false)) {
                if (!descendant.isShadow || !descendant.isShadow()) nextBlocks.add(descendant.id);
            }
        }
        const unchanged = nextBlocks.size === this.lockedBlockIds.size &&
            [...nextBlocks].every(id => this.lockedBlockIds.has(id));
        if (unchanged) return;
        for (const id of this.lockedBlockIds) {
            if (nextBlocks.has(id)) continue;
            const block = ws.getBlockById(id);
            if (block) block.setMovable(true);
        }
        for (const id of nextBlocks) {
            if (this.lockedBlockIds.has(id)) continue;
            const block = ws.getBlockById(id);
            if (block) block.setMovable(false);
        }
        for (const id of this.lockedRootIds) {
            if (!nextRoots.has(id)) this.setBlockLockedVisual(ws.getBlockById(id), false);
        }
        for (const id of nextRoots) {
            if (!this.lockedRootIds.has(id)) this.setBlockLockedVisual(ws.getBlockById(id), true);
        }
        this.lockedBlockIds = nextBlocks;
        this.lockedRootIds = nextRoots;
    }
    setBlockLockedVisual (block, locked) {
        const root = block && block.getSvgRoot && block.getSvgRoot();
        if (root && root.classList) root.classList.toggle('blocklyRemoteLocked', locked);
    }
    isConnectionLocked (connection) {
        const source = connection && connection.getSourceBlock && connection.getSourceBlock();
        return !!(source && this.lockedBlockIds && this.lockedBlockIds.has(source.id));
    }
    onWorkspaceUpdate (data) {
        // When we change sprites, update the toolbox to have the new sprite's blocks
        const toolboxXML = this.getToolboxXML();
        if (toolboxXML) {
            this.props.updateToolboxState(toolboxXML);
        }

        if (this.props.vm.editingTarget && !this.props.workspaceMetrics.targets[this.props.vm.editingTarget.id]) {
            this.onWorkspaceMetricsChange();
        }

        // Disable Blockly events during workspace reload. In Blockly v2, Events.fire()
        // enqueues events for async dispatch (after rendering), so the old pattern of
        // removing and re-adding the blockListener no longer prevents spurious events
        // from reaching the VM — the queued events fire after the listener is re-added.
        // Disabling events entirely during the load ensures nothing is queued.
        this.workspace.removeChangeListener(this.toolboxUpdateChangeListener);
        try {
            this.ScratchBlocks.Events.disable();
            const dom = this.ScratchBlocks.utils.xml.textToDom(data.xml);
            this.ScratchBlocks.clearWorkspaceAndLoadFromXml(dom, this.workspace);
            this.applyBlockCommentLocations();
            this.renderSilently();
        } catch (error) {
            // The workspace is likely incomplete. What did update should be
            // functional.
            //
            // Instead of throwing the error, by logging it and continuing as
            // normal lets the other workspace update processes complete in the
            // gui and vm, which lets the vm run even if the workspace is
            // incomplete. Throwing the error would keep things like setting the
            // correct editing target from happening which can interfere with
            // some blocks and processes in the vm.
            if (error.message) {
                error.message = `Workspace Update Error: ${error.message}`;
            }
            log.error(error);
        } finally {
            this.ScratchBlocks.Events.enable();
        }

        if (this.props.vm.editingTarget && this.props.workspaceMetrics.targets[this.props.vm.editingTarget.id]) {
            const {scrollX, scrollY, scale} = this.props.workspaceMetrics.targets[this.props.vm.editingTarget.id];
            this.workspace.scrollX = scrollX;
            this.workspace.scrollY = scrollY;
            this.workspace.scale = scale;
            this.workspace.resize();
        }

        // Clear the undo state of the workspace since this is a
        // fresh workspace and we don't want any changes made to another sprites
        // workspace to be 'undone' here.
        this.workspace.clearUndo();
        this.lockAllBlocksIfReadOnly();
        // Variable palette entries aren't in the toolbox XML, so a reload with unchanged XML leaves them stale.
        this.requestToolboxUpdate();
        // Let events get flushed before readding the toolbox-updater listener
        // to avoid unneeded refreshes.
        requestAnimationFrame(() => {
            setTimeout(() => {
                this.workspace.addChangeListener(
                    this.toolboxUpdateChangeListener
                );
            });
        });
    }
    handleMonitorsUpdate (monitors) {
        // Update the checkboxes of the relevant monitors.
        // TODO: What about monitors that have fields? See todo in scratch-vm blocks.js changeBlock:
        // https://github.com/LLK/scratch-vm/blob/2373f9483edaf705f11d62662f7bb2a57fbb5e28/src/engine/blocks.js#L569-L576
        const flyout = this.workspace.getFlyout();
        for (const monitor of monitors.values()) {
            const blockId = monitor.get('id');
            const isVisible = monitor.get('visible');
            flyout.setCheckboxState(blockId, isVisible);
            // We also need to update the isMonitored flag for this block on the VM, since it's used to determine
            // whether the checkbox is activated or not when the checkbox is re-displayed (e.g. local variables/blocks
            // when switching between sprites).
            const block = this.props.vm.runtime.monitorBlocks.getBlock(blockId);
            if (block) {
                block.isMonitored = isVisible;
            }
        }
    }
    handleExtensionAdded (categoryInfo) {
        analytics.event({
            category: 'extensions',
            action: 'added',
            label: categoryInfo.id
        });

        const defineBlocks = blockInfoArray => {
            if (blockInfoArray && blockInfoArray.length > 0) {
                const staticBlocksJson = [];
                const dynamicBlocksInfo = [];
                blockInfoArray.forEach(blockInfo => {
                    if (blockInfo.info && blockInfo.info.isDynamic) {
                        dynamicBlocksInfo.push(blockInfo);
                    } else if (blockInfo.json) {
                        staticBlocksJson.push(injectExtensionBlockIcons(blockInfo.json, this.props.colorMode));
                    }
                    // otherwise it's a non-block entry such as '---'
                });

                this.ScratchBlocks.defineBlocksWithJsonArray(staticBlocksJson);
                dynamicBlocksInfo.forEach(blockInfo => {
                    // This is creating the block factory / constructor -- NOT a specific instance of the block.
                    // The factory should only know static info about the block: the category info and the opcode.
                    // Anything else will be picked up from the XML attached to the block instance.
                    const extendedOpcode = `${categoryInfo.id}_${blockInfo.info.opcode}`;
                    const blockDefinition =
                        defineDynamicBlock(this.ScratchBlocks, categoryInfo, blockInfo, extendedOpcode);
                    this.ScratchBlocks.Blocks[extendedOpcode] = blockDefinition;
                });
            }
        };

        // scratch-blocks implements a menu or custom field as a special kind of block ("shadow" block)
        // these actually define blocks and MUST run regardless of the UI state
        defineBlocks(
            Object.getOwnPropertyNames(categoryInfo.customFieldTypes)
                .map(fieldTypeName => categoryInfo.customFieldTypes[fieldTypeName].scratchBlocksDefinition));
        defineBlocks(categoryInfo.menus);
        defineBlocks(categoryInfo.blocks);
        // Note that Blockly uses the UK spelling of "colour", so fields that
        // interact directly with Blockly follow that convention, while Scratch
        // code uses the US spelling of "color".
        let colourPrimary = categoryInfo.color1;
        let colourSecondary = categoryInfo.color2;
        let colourTertiary = categoryInfo.color3;
        let colourQuaternary = categoryInfo.color3;
        if (this.props.colorMode !== DEFAULT_MODE) {
            const colors = getExtensionColors(this.props.colorMode);
            colourPrimary = colors.colourPrimary;
            colourSecondary = colors.colourSecondary;
            colourTertiary = colors.colourTertiary;
            colourQuaternary = colors.colourQuaternary;
        }
        this.ScratchBlocks.getMainWorkspace()
            .getTheme()
            .setBlockStyle(categoryInfo.id, {
                colourPrimary,
                colourSecondary,
                colourTertiary,
                colourQuaternary
            });
        this.ScratchBlocks.getMainWorkspace()
            .getTheme()
            .setBlockStyle(`${categoryInfo.id}_selected`, {
                colourPrimary: colourQuaternary,
                colourSecondary: colourQuaternary,
                colourTertiary: colourQuaternary,
                colourQuaternary: colourQuaternary
            });
        this.ScratchBlocks.getMainWorkspace().setTheme(
            this.ScratchBlocks.getMainWorkspace().getTheme()
        );
        // Update the toolbox with new blocks if possible
        const toolboxXML = this.getToolboxXML();
        if (toolboxXML) {
            this.props.updateToolboxState(toolboxXML);
        }
    }
    handleBlocksInfoUpdate (categoryInfo) {
        // @todo Later we should replace this to avoid all the warnings from redefining blocks.
        this.handleExtensionAdded(categoryInfo);
    }
    handleCategorySelected (categoryId) {
        const extension = extensionData.find(ext => ext.extensionId === categoryId);
        if (extension && extension.launchPeripheralConnectionFlow) {
            this.handleConnectionModalStart(categoryId);
        }

        this.withToolboxUpdates(() => {
            const toolbox = this.workspace.getToolbox();
            toolbox.setSelectedItem(toolbox.getToolboxItemById(categoryId));
        });
    }
    setBlocks (blocks) {
        this.blocks = blocks;
    }
    handlePromptStart (message, defaultValue, callback, optTitle, optVarType) {
        const p = {prompt: {callback, message, defaultValue}};
        p.prompt.title = optTitle ? optTitle :
            this.ScratchBlocks.Msg.VARIABLE_MODAL_TITLE;
        p.prompt.varType = typeof optVarType === 'string' ?
            optVarType : this.ScratchBlocks.SCALAR_VARIABLE_TYPE;
        p.prompt.showVariableOptions = // This flag means that we should show variable/list options about scope
            optVarType !== this.ScratchBlocks.BROADCAST_MESSAGE_VARIABLE_TYPE &&
            p.prompt.title !== this.ScratchBlocks.Msg.RENAME_VARIABLE_MODAL_TITLE &&
            p.prompt.title !== this.ScratchBlocks.Msg.RENAME_LIST_MODAL_TITLE;
        p.prompt.showCloudOption = (optVarType === this.ScratchBlocks.SCALAR_VARIABLE_TYPE) && this.props.canUseCloud;
        this.setState(p);
    }
    handleConnectionModalStart (extensionId) {
        this.props.onOpenConnectionModal(extensionId);
    }
    handleStatusButtonUpdate () {
        this.workspace.getFlyout().refreshStatusButtons();
    }
    handleOpenSoundRecorder () {
        this.props.onOpenSoundRecorder();
    }

    /*
     * Pass along information about proposed name and variable options (scope and isCloud)
     * and additional potentially conflicting variable names from the VM
     * to the variable validation prompt callback used in scratch-blocks.
     */
    handlePromptCallback (input, variableOptions) {
        this.state.prompt.callback(
            input,
            this.props.vm.runtime.getAllVarNamesOfType(this.state.prompt.varType),
            variableOptions);
        this.handlePromptClose();
    }
    handlePromptClose () {
        this.setState({prompt: null});
    }
    handleCustomProceduresClose (data) {
        this.props.onRequestCloseCustomProcedures(data);
        const ws = this.workspace;
        this.updateToolbox();
        ws.getToolbox().selectCategoryByName('myBlocks');
    }
    handleDrop (dragInfo) {
        fetch(dragInfo.payload.bodyUrl)
            .then(response => response.json())
            .then(blocks => this.props.vm.shareBlocksToTarget(blocks, this.props.vm.editingTarget.id))
            .then(() => {
                this.props.vm.refreshWorkspace();
            });
    }
    render () {
         
        const {
            anyModalVisible,
            canUseCloud,
            customProceduresVisible,
            extensionLibraryVisible,
            options,
            stageSize,
            vm,
            isRtl,
            isVisible,
            onActivateColorPicker,
            onOpenConnectionModal,
            onOpenSoundRecorder,
            updateToolboxState,
            onActivateCustomProcedures,
            onRequestCloseExtensionLibrary,
            onRequestCloseCustomProcedures,
            toolboxXML,
            updateMetrics: updateMetricsProp,
            useCatBlocks,
            workspaceMetrics,
            colorMode,
            ...props
        } = this.props;
         
        return (
            <React.Fragment>
                <DroppableBlocks
                    componentRef={this.setBlocks}
                    onDrop={this.handleDrop}
                    {...props}
                />
                {this.state.prompt ? (
                    <Prompt
                        defaultValue={this.state.prompt.defaultValue}
                        isStage={vm.runtime.getEditingTarget().isStage}
                        showListMessage={this.state.prompt.varType === this.ScratchBlocks.LIST_VARIABLE_TYPE}
                        label={this.state.prompt.message}
                        showCloudOption={this.state.prompt.showCloudOption}
                        showVariableOptions={this.state.prompt.showVariableOptions}
                        title={this.state.prompt.title}
                        vm={vm}
                        onCancel={this.handlePromptClose}
                        onOk={this.handlePromptCallback}
                    />
                ) : null}
                {extensionLibraryVisible ? (
                    <ExtensionLibrary
                        vm={vm}
                        onCategorySelected={this.handleCategorySelected}
                        onRequestClose={onRequestCloseExtensionLibrary}
                    />
                ) : null}
                {customProceduresVisible ? (
                    <CustomProcedures
                        options={{
                            media: options.media
                        }}
                        onRequestClose={this.handleCustomProceduresClose}
                        colorMode={colorMode}
                    />
                ) : null}
            </React.Fragment>
        );
    }
}

Blocks.propTypes = {
    anyModalVisible: PropTypes.bool,
    canUseCloud: PropTypes.bool,
    customProceduresVisible: PropTypes.bool,
    extensionLibraryVisible: PropTypes.bool,
    isRtl: PropTypes.bool,
    isVisible: PropTypes.bool,
    readOnly: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    messages: PropTypes.objectOf(PropTypes.string),
    onActivateColorPicker: PropTypes.func,
    onActivateCustomProcedures: PropTypes.func,
    onOpenConnectionModal: PropTypes.func,
    onOpenSoundRecorder: PropTypes.func,
    onRequestCloseCustomProcedures: PropTypes.func,
    onRequestCloseExtensionLibrary: PropTypes.func,
    options: PropTypes.shape({
        media: PropTypes.string,
        zoom: PropTypes.shape({
            controls: PropTypes.bool,
            wheel: PropTypes.bool,
            startScale: PropTypes.number
        }),
        comments: PropTypes.bool,
        collapse: PropTypes.bool
    }),
    stageSize: PropTypes.oneOf(Object.keys(STAGE_DISPLAY_SIZES)).isRequired,
    colorMode: PropTypes.oneOf(Object.keys(colorModeMap)),
    toolboxXML: PropTypes.string,
    updateMetrics: PropTypes.func,
    updateToolboxState: PropTypes.func,
    useCatBlocks: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired,
    workspaceMetrics: PropTypes.shape({
        targets: PropTypes.objectOf(PropTypes.object)
    })
};

Blocks.defaultOptions = {
    zoom: {
        controls: true,
        wheel: true,
        pinch: true,
        startScale: BLOCKS_DEFAULT_SCALE
    },
    move: {
        wheel: true
    },
    grid: {
        spacing: 40,
        length: 2,
        colour: '#ddd'
    },
    comments: true,
    collapse: false,
    sounds: false,
    trashcan: false,
    modalInputs: false
};

Blocks.defaultProps = {
    isVisible: true,
    options: Blocks.defaultOptions,
    colorMode: DEFAULT_MODE
};

const mapStateToProps = state => ({
    anyModalVisible: (
        Object.keys(state.scratchGui.modals).some(key => state.scratchGui.modals[key]) ||
        state.scratchGui.mode.isFullScreen
    ),
    extensionLibraryVisible: state.scratchGui.modals.extensionLibrary,
    isRtl: state.locales.isRtl,
    locale: state.locales.locale,
    messages: state.locales.messages,
    toolboxXML: state.scratchGui.toolbox.toolboxXML,
    customProceduresVisible: state.scratchGui.customProcedures.active,
    workspaceMetrics: state.scratchGui.workspaceMetrics,
    useCatBlocks: isTimeTravel2020(state) || state.scratchGui.settings.theme === CAT_BLOCKS_THEME
});

const mapDispatchToProps = dispatch => ({
    onActivateColorPicker: callback => dispatch(activateColorPicker(callback)),
    onActivateCustomProcedures: (data, callback) => dispatch(activateCustomProcedures(data, callback)),
    onOpenConnectionModal: id => {
        dispatch(setConnectionModalExtensionId(id));
        dispatch(openConnectionModal());
    },
    onOpenSoundRecorder: () => {
        dispatch(activateTab(SOUNDS_TAB_INDEX));
        dispatch(openSoundRecorder());
    },
    onRequestCloseExtensionLibrary: () => {
        dispatch(closeExtensionLibrary());
    },
    onRequestCloseCustomProcedures: data => {
        dispatch(deactivateCustomProcedures(data));
    },
    updateToolboxState: toolboxXML => {
        dispatch(updateToolbox(toolboxXML));
    },
    updateMetrics: metrics => {
        dispatch(updateMetrics(metrics));
    }
});

export {Blocks};
export default errorBoundaryHOC('Blocks')(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(Blocks)
);
